// Eased window scroll (ease-in-out, 450 ms by default): jumps under prefers-reduced-motion and
// stops when the user scrolls on their own. Resolves when the scroll ends or is stopped.
//
// Own easing rather than scrollIntoView({ behavior: 'smooth' }): src/js/smoothscroll.js overrides
// smooth scrolling app-wide and throws outside the collection-details screen. The page's own scroll
// snapping (the landing's phone feed) is held off while it runs, or every frame would snap.

let stopCurrent: (() => void) | undefined;

export function easeWindowScrollTo(targetY: number, durationMs = 450): Promise<void> {
  stopCurrent?.();

  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const endY = Math.min(Math.max(0, Math.round(targetY)), maxY);
  const startY = window.scrollY;
  const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || Math.abs(endY - startY) < 2) {
    window.scrollTo(0, endY);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const startedAt = performance.now();
    const page = document.documentElement;
    const snap = page.style.scrollSnapType;
    page.style.scrollSnapType = 'none';
    let frame = 0;
    const stop = (): void => {
      cancelAnimationFrame(frame);
      page.style.scrollSnapType = snap;
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      if (stopCurrent === stop) {
        stopCurrent = undefined;
      }
      resolve();
    };
    const step = (now: number): void => {
      const t = Math.min(1, (now - startedAt) / durationMs);
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo(0, Math.round(startY + (endY - startY) * eased));
      if (t < 1) {
        frame = requestAnimationFrame(step);
      } else {
        stop();
      }
    };

    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    stopCurrent = stop;
    frame = requestAnimationFrame(step);
  });
}

// The same easing inside a scrolling box (the texture list's "↓ 9 more"), stopped by a wheel or a
// touch on the box.
export function easeElementScrollTo(box: HTMLElement, targetTop: number, durationMs = 350): void {
  const endTop = Math.min(Math.max(0, Math.round(targetTop)), Math.max(0, box.scrollHeight - box.clientHeight));
  const startTop = box.scrollTop;
  const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || Math.abs(endTop - startTop) < 2) {
    box.scrollTop = endTop;
    return;
  }
  const startedAt = performance.now();
  let frame = 0;
  const stop = (): void => {
    cancelAnimationFrame(frame);
    box.removeEventListener('wheel', stop);
    box.removeEventListener('touchstart', stop);
  };
  const step = (now: number): void => {
    const t = Math.min(1, (now - startedAt) / durationMs);
    const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    box.scrollTop = Math.round(startTop + (endTop - startTop) * eased);
    if (t < 1) {
      frame = requestAnimationFrame(step);
    } else {
      stop();
    }
  };
  box.addEventListener('wheel', stop, { passive: true });
  box.addEventListener('touchstart', stop, { passive: true });
  frame = requestAnimationFrame(step);
}

// The render pages always keep their scrollbar, so nothing shifts by a classic scrollbar's width
// between the long landing and a short step (Windows: right-aligned controls always, and the
// wordmark too once the window is wider than the 1920px frame). Overlay scrollbars (macOS,
// phones) take no room either way. Counted, because the landing and the flow hand over to each
// other in either order.
let scrollbarHolds = 0;

export function holdPageScrollbar(document: Document): () => void {
  scrollbarHolds += 1;
  document.documentElement.style.setProperty('overflow-y', 'scroll');
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    scrollbarHolds -= 1;
    if (scrollbarHolds === 0) {
      document.documentElement.style.removeProperty('overflow-y');
    }
  };
}
